import React, { Fragment, PureComponent } from 'react';

import Avatar from '@atlaskit/avatar';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';
import emphasizeMatches from '@atlassian/bitkit-code/src/utils/emphasize-matches';

import { CodeSearchResult, Segment } from 'src/types/search';
import { publishFact, publishUiEvent } from 'src/utils/analytics/publish';
import generateCodeLineHash from 'src/utils/code-line-url-hash';

import {
  SearchResultsSourceFileClickedFact,
  SearchResultsSourcePathClickedFact,
} from '../../facts';
import getMatchLineNumbers from '../../utils/get-match-line-numbers';
import getMatchRanges from '../../utils/get-match-ranges';

import SearchContentHit from './search-content-hit';
import SearchPathHit from './search-path-hit';
import * as styles from './search-result.style';
import { srcHref, PATH_SEPARATOR } from './utils';

type ResultProps = {
  pageNum: number;
  result: CodeSearchResult;
  resultNum: number;
  accountUuid: string;
  displayRepoName: boolean;
  directoryPathClickable: boolean;
};

enum PathType {
  FILE = 'FILE',
  DIR = 'DIR',
}

type PathSegment = {
  type: PathType;
  name: string;
  path: string;
};

const parsePath = (path: string): PathSegment[] => {
  let pathSoFar = '';
  const rawSegments = path.split(PATH_SEPARATOR);
  const segments = rawSegments.map((seg, i) => {
    const type = i === rawSegments.length - 1 ? PathType.FILE : PathType.DIR;
    pathSoFar += seg + (type === PathType.DIR ? '/' : '');
    return {
      type,
      name: seg,
      path: pathSoFar,
    };
  });

  return segments;
};

export default class SearchResult extends PureComponent<ResultProps> {
  handleSegmentFileClick = () => {
    const { pageNum, resultNum, accountUuid } = this.props;

    publishFact(
      new SearchResultsSourceFileClickedFact({
        result_num: resultNum,
        page_num: pageNum,
      })
    );
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'sourceFileLink',
      source: 'search',
      containerType: 'account',
      containerId: uncurlyUuid(accountUuid),
      attributes: {
        result_num: resultNum,
        page_num: pageNum,
      },
    });
  };

  handleSegmentSourcePathClick = () => {
    const { pageNum, resultNum, accountUuid } = this.props;

    publishFact(
      new SearchResultsSourcePathClickedFact({
        result_num: resultNum,
        page_num: pageNum,
      })
    );
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'sourcePathLink',
      source: 'search',
      containerType: 'account',
      containerId: uncurlyUuid(accountUuid),
      attributes: {
        result_num: resultNum,
        page_num: pageNum,
      },
    });
  };

  // eslint-disable-next-line consistent-return
  renderSegment(segment: PathSegment) {
    const { result, directoryPathClickable } = this.props;

    if (result.file.commit.hash === undefined) {
      return segment.name;
    } else if (segment.type === 'DIR') {
      const url = srcHref(result.file.commit, segment.path) || '';
      if (directoryPathClickable) {
        return (
          <styles.DirLink
            onClick={this.handleSegmentSourcePathClick}
            href={url}
          >
            {segment.name}
          </styles.DirLink>
        );
      } else {
        return <span>{segment.name}</span>;
      }
    } else if (segment.type === 'FILE') {
      const url = srcHref(result.file.commit, segment.path);
      const hash = generateCodeLineHash(
        segment.name,
        getMatchLineNumbers(result)
      );
      const fragment = hash ? `#${hash}` : '';
      const href = url ? `${url}${fragment}` : '';
      return (
        <styles.FileLink onClick={this.handleSegmentFileClick} href={href}>
          {segment.name}
        </styles.FileLink>
      );
    }
  }

  renderPath(path: string, pathMatches: Segment[]) {
    const segments = parsePath(path).map(segment => {
      // We want all the Segments wrapped in a span, so if they wrap into a second line in the UI
      // the `/` will wrap with the path segment.
      if (segment.type === 'FILE') {
        return <span key={segment.path}>{this.renderSegment(segment)}</span>;
      }
      return (
        <span key={segment.path}>
          {this.renderSegment(segment)}
          <styles.PathSeperator>/</styles.PathSeperator>
        </span>
      );
    });

    return emphasizeMatches(
      <Fragment>{segments}</Fragment>,
      getMatchRanges(pathMatches)
    );
  }

  render() {
    const { result, displayRepoName } = this.props;
    const {
      file: {
        path,
        commit: { repository },
      },
      content_matches: contentMatches,
      path_matches: pathMatches,
    } = result;

    const title = (
      <Fragment>
        {displayRepoName && (
          <Avatar
            appearance="square"
            size="small"
            borderColor="transparent"
            src={repository.links.avatar.href}
            href={repository.links.html.href}
          />
        )}

        <styles.FileLinkWrapper>
          {displayRepoName && (
            <styles.repositorySubtleLink
              onClick={this.handleSegmentSourcePathClick}
              repository={result.file.commit.repository}
            >
              <styles.RepositoryName>
                {result.file.commit.repository.full_name}
              </styles.RepositoryName>
            </styles.repositorySubtleLink>
          )}
          {this.renderPath(path, pathMatches || [])}
        </styles.FileLinkWrapper>
      </Fragment>
    );

    if (contentMatches.length !== 0) {
      return <SearchContentHit {...this.props} title={title} />;
    }
    return <SearchPathHit {...this.props} title={title} />;
  }
}
